<template>
  <div
    class="min-h-screen flex items-center justify-center py-10 px-4 sm:px-6 lg:px-8 relative overflow-hidden bg-gradient-to-br from-slate-50 via-gray-50 to-indigo-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-slate-900"
  >
    <!-- Ambient glowing backdrop orbs -->
    <div class="fixed inset-0 pointer-events-none overflow-hidden">
      <div
        class="absolute -top-40 -right-40 w-96 h-96 bg-purple-500/15 dark:bg-purple-900/20 rounded-full blur-3xl animate-pulse"
      ></div>
      <div
        class="absolute top-1/2 -left-40 w-96 h-96 bg-blue-500/15 dark:bg-blue-900/20 rounded-full blur-3xl"
      ></div>
      <div
        class="absolute -bottom-40 right-1/3 w-96 h-96 bg-indigo-500/15 dark:bg-indigo-900/20 rounded-full blur-3xl"
      ></div>
    </div>

    <!-- Main Card -->
    <div
      class="relative w-full max-w-md bg-white/95 dark:bg-gray-900/90 backdrop-blur-xl rounded-3xl border border-gray-100/80 dark:border-gray-800/80 p-8 sm:p-10 transition-all duration-300"
    >
      <!-- Header -->
      <div class="text-center mb-8">
        <!-- Floating Security Emblem -->
        <div class="relative inline-flex mb-5">
          <div
            class="w-16 h-16 rounded-2xl bg-gradient-to-tr from-indigo-600 via-indigo-500 to-purple-600 flex items-center justify-center ring-4 ring-indigo-50 dark:ring-indigo-950/40"
          >
            <KeyRound class="w-8 h-8 text-white" />
          </div>
          <div
            class="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-emerald-500 border-2 border-white dark:border-gray-900 flex items-center justify-center"
          >
            <ShieldCheck class="w-3.5 h-3.5 text-white" />
          </div>
        </div>

        <div class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-50 dark:bg-indigo-950/50 border border-indigo-100/80 dark:border-indigo-800/40 text-xs font-semibold text-indigo-700 dark:text-indigo-300 mb-3">
          <span>Security & Recovery</span>
        </div>

        <h2 class="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">
          Reset Password
        </h2>
        <p class="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1.5 max-w-xs mx-auto">
          {{
            currentStep === 1
              ? "Enter your registered email or phone to receive a verification OTP"
              : "Verify your identity and create your new secure password"
          }}
        </p>

        <!-- Step Indicator -->
        <div class="flex items-center justify-center gap-2 mt-4">
          <div
            :class="[
              'h-1.5 rounded-full transition-all duration-300',
              currentStep === 1
                ? 'w-8 bg-indigo-600 dark:bg-indigo-500'
                : 'w-3 bg-emerald-500'
            ]"
          ></div>
          <div
            :class="[
              'h-1.5 rounded-full transition-all duration-300',
              currentStep === 2
                ? 'w-8 bg-indigo-600 dark:bg-indigo-500'
                : 'w-3 bg-gray-200 dark:bg-gray-700'
            ]"
          ></div>
        </div>
      </div>

      <!-- Success Popup Modal -->
      <transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
      >
        <div
          v-if="showSuccessPopup"
          class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        >
          <div
            class="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 p-8 rounded-3xl max-w-sm w-full mx-4 text-center transform transition-all"
          >
            <div
              class="w-16 h-16 mx-auto bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 rounded-2xl flex items-center justify-center mb-5"
            >
              <CheckCircle2 class="w-9 h-9 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-1.5">
              OTP Dispatched!
            </h3>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              A 6-digit verification code has been sent. Please check your inbox or phone.
            </p>
          </div>
        </div>
      </transition>

      <!-- Final Success Banner -->
      <transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0 -translate-y-2"
        enter-to-class="opacity-100 translate-y-0"
      >
        <div
          v-if="showFinalSuccess"
          class="mb-6 bg-emerald-50/90 dark:bg-emerald-950/40 border border-emerald-200/80 dark:border-emerald-800/60 rounded-2xl p-4"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center flex-shrink-0"
            >
              <CheckCircle2 class="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p class="text-sm font-bold text-emerald-900 dark:text-emerald-200">
                Password Reset Successful!
              </p>
              <p class="text-xs text-emerald-700 dark:text-emerald-400">
                Redirecting to sign-in page...
              </p>
            </div>
          </div>
        </div>
      </transition>

      <!-- Step 1: Identifier Input Form -->
      <form
        v-if="currentStep === 1"
        @submit.prevent="sendOtp"
        class="space-y-5"
      >
        <div>
          <label
            for="identifier"
            class="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-2"
          >
            Email Address or Phone Number
          </label>
          <div class="relative group">
            <div
              class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-indigo-600 dark:group-focus-within:text-indigo-400 transition-colors"
            >
              <Mail v-if="isEmailInput" class="w-5 h-5" />
              <Phone v-else-if="isPhoneInput" class="w-5 h-5" />
              <KeyRound v-else class="w-5 h-5" />
            </div>
            <input
              id="identifier"
              v-model="identifier"
              name="identifier"
              type="text"
              required
              class="block w-full pl-11 pr-4 py-3.5 bg-gray-50/80 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-400 transition-all text-sm"
              placeholder="e.g. name@school.edu or 9876543210"
            />
          </div>
          <p class="text-xs text-gray-400 dark:text-gray-500 mt-2">
            Tip: We'll automatically route the verification code to your registered contact channel.
          </p>
        </div>

        <!-- Error Notification -->
        <transition
          enter-active-class="transition duration-150 ease-out"
          enter-from-class="opacity-0 -translate-y-1"
          enter-to-class="opacity-100 translate-y-0"
        >
          <div
            v-if="error"
            class="flex items-start gap-2.5 p-3.5 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/60 rounded-xl text-red-700 dark:text-red-300 text-xs"
          >
            <AlertCircle class="w-4 h-4 flex-shrink-0 mt-0.5 text-red-500" />
            <span>{{ error }}</span>
          </div>
        </transition>

        <!-- Submit Button -->
        <button
          type="submit"
          :disabled="loading || !identifier"
          class="w-full flex justify-center items-center gap-2 py-3.5 px-4 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 via-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 border border-transparent"
        >
          <Loader2 v-if="loading" class="w-4 h-4 animate-spin" />
          <span>{{ loading ? "Dispatching OTP..." : "Send Verification Code" }}</span>
        </button>
      </form>

      <!-- Step 2: OTP and Password Reset Form -->
      <form
        v-if="currentStep === 2"
        @submit.prevent="resetPassword"
        class="space-y-5"
      >
        <!-- Contact Target Chip -->
        <div
          class="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/60 border border-gray-200/80 dark:border-gray-700/80 rounded-xl"
        >
          <div class="flex items-center gap-2.5 truncate">
            <div
              class="w-7 h-7 rounded-lg bg-indigo-100 dark:bg-indigo-950/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400 flex-shrink-0"
            >
              <Mail v-if="isEmailInput" class="w-4 h-4" />
              <Phone v-else class="w-4 h-4" />
            </div>
            <div class="truncate">
              <p class="text-[10px] uppercase font-semibold text-gray-400 dark:text-gray-500">
                Code sent to
              </p>
              <p class="text-xs font-bold text-gray-800 dark:text-gray-200 truncate">
                {{ identifier }}
              </p>
            </div>
          </div>
          <button
            type="button"
            @click="goBackToStep1"
            class="text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline flex-shrink-0 ml-2"
          >
            Change
          </button>
        </div>

        <!-- 6-Digit OTP Field -->
        <div>
          <label
            for="otp"
            class="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-2"
          >
            6-Digit Verification Code
          </label>
          <div class="relative group">
            <input
              id="otp"
              v-model="otp"
              name="otp"
              type="text"
              required
              maxlength="6"
              pattern="[0-9]*"
              autocomplete="one-time-code"
              class="block w-full py-3.5 bg-gray-50/80 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-400 transition-all text-center tracking-[0.4em] font-mono text-lg font-bold"
              placeholder="000000"
            />
          </div>
        </div>

        <!-- New Password Field -->
        <div>
          <label
            for="newPassword"
            class="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-2"
          >
            New Password
          </label>
          <div class="relative group">
            <div
              class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-indigo-600 dark:group-focus-within:text-indigo-400 transition-colors"
            >
              <Lock class="w-5 h-5" />
            </div>
            <input
              id="newPassword"
              v-model="newPassword"
              name="newPassword"
              :type="showNewPassword ? 'text' : 'password'"
              required
              minlength="6"
              class="block w-full pl-11 pr-11 py-3.5 bg-gray-50/80 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-400 transition-all text-sm"
              placeholder="At least 6 characters"
            />
            <button
              type="button"
              @click="showNewPassword = !showNewPassword"
              class="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            >
              <EyeOff v-if="showNewPassword" class="w-4 h-4" />
              <Eye v-else class="w-4 h-4" />
            </button>
          </div>
        </div>

        <!-- Confirm Password Field -->
        <div>
          <label
            for="confirmPassword"
            class="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-2"
          >
            Confirm New Password
          </label>
          <div class="relative group">
            <div
              class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-indigo-600 dark:group-focus-within:text-indigo-400 transition-colors"
            >
              <Lock class="w-5 h-5" />
            </div>
            <input
              id="confirmPassword"
              v-model="confirmPassword"
              name="confirmPassword"
              :type="showConfirmPassword ? 'text' : 'password'"
              required
              minlength="6"
              :class="[
                'block w-full pl-11 pr-11 py-3.5 bg-gray-50/80 dark:bg-gray-800/60 border rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 transition-all text-sm',
                confirmPassword && newPassword !== confirmPassword
                  ? 'border-red-300 dark:border-red-800 focus:ring-red-500/20 focus:border-red-500'
                  : 'border-gray-200 dark:border-gray-700 focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-400'
              ]"
              placeholder="Re-enter your new password"
            />
            <button
              type="button"
              @click="showConfirmPassword = !showConfirmPassword"
              class="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            >
              <EyeOff v-if="showConfirmPassword" class="w-4 h-4" />
              <Eye v-else class="w-4 h-4" />
            </button>
          </div>
          <p
            v-if="confirmPassword && newPassword !== confirmPassword"
            class="text-red-600 dark:text-red-400 text-xs mt-1.5 flex items-center gap-1"
          >
            <AlertCircle class="w-3.5 h-3.5" />
            <span>Passwords do not match</span>
          </p>
        </div>

        <!-- Error Notification -->
        <transition
          enter-active-class="transition duration-150 ease-out"
          enter-from-class="opacity-0 -translate-y-1"
          enter-to-class="opacity-100 translate-y-0"
        >
          <div
            v-if="error"
            class="flex items-start gap-2.5 p-3.5 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/60 rounded-xl text-red-700 dark:text-red-300 text-xs"
          >
            <AlertCircle class="w-4 h-4 flex-shrink-0 mt-0.5 text-red-500" />
            <span>{{ error }}</span>
          </div>
        </transition>

        <!-- Action Buttons -->
        <div class="flex gap-3 pt-2">
          <button
            type="button"
            @click="goBackToStep1"
            class="w-1/3 py-3.5 px-3 border border-gray-200 dark:border-gray-700 text-xs font-semibold rounded-xl text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors duration-150"
          >
            Back
          </button>
          <button
            type="submit"
            :disabled="
              loading ||
              !otp ||
              !newPassword ||
              !confirmPassword ||
              newPassword !== confirmPassword
            "
            class="w-2/3 flex justify-center items-center gap-2 py-3.5 px-4 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 border border-transparent"
          >
            <Loader2 v-if="loading" class="w-4 h-4 animate-spin" />
            <span>{{ loading ? "Updating..." : "Set New Password" }}</span>
          </button>
        </div>
      </form>

      <!-- Footer -->
      <div class="mt-8 pt-6 border-t border-gray-100 dark:border-gray-800/80 text-center space-y-3">
        <router-link
          to="/login"
          class="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
        >
          <ArrowLeft class="w-3.5 h-3.5" />
          <span>Return to Sign In</span>
        </router-link>

        <div class="flex items-center justify-center gap-1.5 text-[11px] text-gray-400 dark:text-gray-500">
          <ShieldCheck class="w-3.5 h-3.5 text-emerald-500" />
          <span>Protected by 256-bit SSL Security Protocol</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed } from "vue";
  import { useRouter } from "vue-router";
  import {
    KeyRound,
    ShieldCheck,
    Mail,
    Phone,
    Lock,
    Eye,
    EyeOff,
    CheckCircle2,
    AlertCircle,
    Loader2,
    ArrowLeft,
  } from "lucide-vue-next";

  const router = useRouter();
  const baseURL = process.env.VUE_APP_BASE_URL || "http://localhost:5000";

  // Reactive state
  const currentStep = ref(1);
  const identifier = ref("");
  const otp = ref("");
  const newPassword = ref("");
  const confirmPassword = ref("");
  const showNewPassword = ref(false);
  const showConfirmPassword = ref(false);
  const loading = ref(false);
  const error = ref("");
  const showSuccessPopup = ref(false);
  const showFinalSuccess = ref(false);

  // Helper validators
  const isEmail = (value) => /\S+@\S+\.\S+/.test(value || "");
  const isPhone = (value) =>
    /^(?:\+91[\s-]?)?[6-9]\d{9}$/.test((value || "").replace(/\s+/g, ""));

  const isEmailInput = computed(() => isEmail(identifier.value));
  const isPhoneInput = computed(() => isPhone(identifier.value));

  // Password validity
  const isPasswordValid = computed(() => {
    return (
      newPassword.value &&
      confirmPassword.value &&
      newPassword.value === confirmPassword.value
    );
  });

  // Send OTP
  const sendOtp = async () => {
    loading.value = true;
    error.value = "";

    try {
      let payload = {};

      if (isEmail(identifier.value)) {
        payload.email = identifier.value.trim();
      } else if (isPhone(identifier.value)) {
        payload.phone = identifier.value.replace(/\D/g, "").slice(-10);
      } else {
        error.value = "Please enter a valid email or 10-digit phone number.";
        loading.value = false;
        return;
      }

      const response = await fetch(`${baseURL}/api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok && data.message?.includes("OTP sent")) {
        showSuccessPopup.value = true;
        setTimeout(() => {
          showSuccessPopup.value = false;
          currentStep.value = 2;
        }, 2000);
      } else {
        error.value = data.error || "Failed to send OTP.";
      }
    } catch (err) {
      error.value = "Network error. Please try again.";
    } finally {
      loading.value = false;
    }
  };

  // Reset password
  const resetPassword = async () => {
    if (!isPasswordValid.value) {
      error.value = "Passwords do not match.";
      return;
    }

    loading.value = true;
    error.value = "";

    try {
      let payload = {
        otp: otp.value.trim(),
        newPassword: newPassword.value,
      };

      if (isEmail(identifier.value)) {
        payload.email = identifier.value.trim();
      } else if (isPhone(identifier.value)) {
        payload.phone = identifier.value.replace(/\D/g, "").slice(-10);
      } else {
        error.value = "Invalid email or phone.";
        loading.value = false;
        return;
      }

      const response = await fetch(`${baseURL}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok && data.message?.includes("Password reset successful")) {
        showFinalSuccess.value = true;
        setTimeout(() => {
          router.push("/login");
        }, 2000);
      } else {
        error.value = data.error || "Failed to reset password.";
      }
    } catch (err) {
      error.value = "Network error. Please try again.";
    } finally {
      loading.value = false;
    }
  };

  // Go back to Step 1
  const goBackToStep1 = () => {
    currentStep.value = 1;
    otp.value = "";
    newPassword.value = "";
    confirmPassword.value = "";
    error.value = "";
  };
</script>
